import { Component } from '@angular/core';
import { ContactFormInterface } from 'model/interface';
import { CountryCode } from 'model/type';
import { STATE_OPTIONS } from 'model/constant';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  FormControl,
  FormArray
} from '@angular/forms';
import { PhoneFormatDirective } from 'src/app/directive';
import { ContactService, AnalyticsService } from 'src/app/service';
import { RouterLink, RouterOutlet } from '@angular/router';
type PreferredContactMethod = 'email' | 'phone' | 'text';

import { environment } from 'src/environments/environment';
@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, PhoneFormatDirective, RouterLink],
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss']
})
export class HomeComponent {
  readonly assetsUrl = environment.assetsUrl;
  readonly expandedFaqs = new Set<string>();
  
  formSubmitted: boolean = false;
  constructor(
    private fb: FormBuilder,
    private contactSvc: ContactService,
    private analyticsSvc: AnalyticsService
  ) { }

  states = STATE_OPTIONS
  contactForm!: ReturnType<FormBuilder['group']>;
  submittedPayload: ContactFormInterface | null = null;


  contactMethods: { label: string; value: PreferredContactMethod }[] = [
    { label: 'Email', value: 'email' },
    { label: 'Phone', value: 'phone' },
    { label: 'Text', value: 'text' },
  ];



  readonly countries: CountryCode[] = ['US', 'CA', 'EU'];
  ngOnInit(): void {
    this.contactForm = this.fb.nonNullable.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      address: ['', [Validators.required]],
      address2: [''],
      city: ['', [Validators.required]],
      state: ['', [Validators.required]],
      zipCode: [
        '',
        [
          Validators.required,
          Validators.pattern(/^\d{5}(-\d{4})?$/),
        ],
      ],
      county: [''],
      subdivision: [''],
      email: ['', [Validators.required, Validators.email]],
      country: ['US' as CountryCode, [Validators.required]],
      phoneNumber: [
        '',
        [
          Validators.required,
          Validators.pattern(/^[0-9+\-().\s]{7,20}$/),
        ],
      ],
      message: ['', [Validators.required]],
      customerType: ['', [Validators.required]],
      preferredContactMethod: this.fb.nonNullable.control<PreferredContactMethod[]>(
        [],
        [Validators.required]
      ),
      agreeToPolicies: [false, [Validators.requiredTrue]],
      smsOptIn: [false],
    });
  }

  get preferredContactMethod(): FormArray<FormControl<PreferredContactMethod>> {
    return this.contactForm.controls['preferredContactMethod'] as FormArray<FormControl<PreferredContactMethod>>;
  }

  submitContactForm(payload: ContactFormInterface): ContactFormInterface {
    // Replace this later with HttpClient POST:
    // return this.http.post<ContactFormPayload>('/api/contact', payload);

    console.log('API-ready payload:', payload);
    this.analyticsSvc.logEvent('contact_form_api_submission_attempt', { 'payload': JSON.stringify(payload) });
    this.contactSvc.postContactItem(payload)
      .subscribe({
        next: (response) => {
          console.log('Contact form submitted successfully:', response);
          this.analyticsSvc.logEvent('contact_form_api_submission_success', { 'response': JSON.stringify(response) });
          this.formSubmitted = true;
          this.contactForm.reset();
          setTimeout(() => {
            this.formSubmitted = false;
          }, 10000);
        },
        error: (error) => {
          console.error('Error submitting contact form:', error)
          this.analyticsSvc.logEvent('contact_form_api_submission_error', { 'error': JSON.stringify(error) });
        },
        complete: () => console.log('Contact form submission complete')
      });
    return payload;
  }

  onSubmit(event: SubmitEvent): void {
    const rawValue = this.contactForm.getRawValue();
    console.log('Form submitted', event);
    console.log('Form values:', rawValue);
    console.log('form', this.contactForm);
    this.contactForm.markAllAsTouched();

    if (this.contactForm.invalid) {
      return;
    }


    const payload: ContactFormInterface = {
      firstName: rawValue.firstName,
      lastName: rawValue.lastName,
      address: rawValue.address,
      address2: rawValue.address2,
      city: rawValue.city,
      state: rawValue.state,
      zipCode: rawValue.zipCode,
      county: rawValue.county,
      subdivision: rawValue.subdivision,
      email: rawValue.email,
      phone: {
        country: rawValue.country,
        number: rawValue.phoneNumber,
      },
      message: rawValue.message,
      preferredContactMethod: rawValue.preferredContactMethod,
      customerType: rawValue.customerType,
      agreedToPolicies: rawValue.agreeToPolicies,
      agreedToTerms: rawValue.agreeToPolicies,
      agreedToPrivacyPolicy: rawValue.agreeToPolicies,
      smsOptIn: rawValue.smsOptIn,
    };
    this.analyticsSvc.logEvent('contact_form_submitted', { 'payload': JSON.stringify(payload) });
    this.submittedPayload = this.submitContactForm(payload);

  }

  hasError(controlName: keyof typeof this.contactForm.controls): boolean {
    const control = this.contactForm.controls[controlName];
    return control.invalid && (control.dirty || control.touched);
  }

  isFaqExpanded(elementId: string): boolean {
    return this.expandedFaqs.has(elementId);
  }

  buttonClicked(elementId: string): void {
    this.analyticsSvc.logEvent('faq_button_clicked', { elementId });
    console.log('button clicked', elementId);

    if (this.expandedFaqs.has(elementId)) {
      this.expandedFaqs.delete(elementId);
      return;
    }

    this.expandedFaqs.add(elementId);
  }

  onPreferredContactMethodChange(
    event: Event,
    method: PreferredContactMethod
  ): void {
    const checked = (event.target as HTMLInputElement).checked;
    const control = this.contactForm.controls['preferredContactMethod'];

    const current = control.value;

    const next = checked
      ? [...current, method]
      : current.filter((value: PreferredContactMethod) => value !== method);

    control.setValue(next);
    control.markAsTouched();
    control.updateValueAndValidity();
  }

  isPreferredContactMethodSelected(method: PreferredContactMethod): boolean {
    const control = this.contactForm.controls['preferredContactMethod'];
    return control.value.includes(method);
  }
}
